import { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../components/base';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../components/container';
import { BaseShowDetail } from '../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../context/base/eBaseActionStatus';
import { actions } from '../../context/stores/rootActions';
import { AppState } from '../../context/stores/rootReducers';
import BannerPositionDetail from './BannerPositionDetail';
import { BannerPositionModel } from '../../context/models/banners/BannerPositionModel';
import { BannerPositionActionTypes } from '../../context/stores/banner-position/IBannerPositionActionsTypes';

type IBannerPositionPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allBannerPositions: BannerPositionModel[];
	selectedIds: number[];
	showDetail: BaseShowDetail<BannerPositionModel>;
	showConfirm: boolean;
	handleReloadAllData: () => void;
	handleSaveData: (data: BannerPositionModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<BannerPositionModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Vị trí treo Banner`,
		status: state.bannerPosition.status,
		allBannerPositions: state.bannerPosition.allBannerPositions,
		selectedIds: state.bannerPosition.selectedIds,
		showDetail: state.bannerPosition.showDetail,
		showConfirm: state.bannerPosition.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<BannerPositionActionTypes>) => ({
	handleReloadAllData: () => {
		dispatch(actions.bannerPosition.getAllRequest());
	},
	handleSaveData: (data: BannerPositionModel) => {
		dispatch(actions.bannerPosition.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.bannerPosition.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.bannerPosition.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<BannerPositionModel>) => {
		dispatch(actions.bannerPosition.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.bannerPosition.showConfirm(isShow));
	},
});

const BannerPositionPage = (props: IBannerPositionPageProps) => {
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allBannerPositions,
		handleReloadAllData,
		handleDeleteData,
		onChangeSelectedIds,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
	} = props;

	useEffect(() => {
		handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadAllData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<BannerPositionModel>[] => {
		return [
			{ accessorKey: 'name', header: 'Tên vị trí', minSize: 150 },
			{ accessorKey: 'code', header: 'Mã vị trí', minSize: 150 },
			{ accessorKey: 'width', header: 'Chiều rộng', minSize: 150 },
			{ accessorKey: 'height', header: 'Chiều cao', minSize: 150 },
			{
				accessorKey: 'inActive',
				header: 'Trạng thái',
				size: 100,
				cell: ({ getValue }) => {
					const val = getValue() as boolean;
					return <span className={!val ? "text-primary/90 font-medium" : "text-destructive/90 font-medium"}>{!val ? 'Áp dụng' : 'Không'}</span>;
				}
			},
			{
				id: 'actions',
				header: 'Sửa',
				meta: { align: 'center' },
				size: 60,
				cell: ({ row }) => (
					<ButtonLink onClick={() => onShowDetail({ isShow: true, detailData: row.original })} title='Sửa' icon='ni ni-edit' theme='link' />
				)
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<li>
								<Button
									text={`Xóa (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='hidden md:inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									onClick={() => onShowConfirm(true)}
								/>
							</li>

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={allBannerPositions}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={allBannerPositions.length}
								enableGlobalFilter
								toolbar={{ isShowSearch: true, searchPlaceholder: `Tìm kiếm...` }}
								onRefresh={handleReloadAllData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (	
				<>
					<BannerPositionDetail
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(BannerPositionPage);
